import { useEffect, useMemo } from 'react'

import { useHistory, useLocation } from 'react-router-dom'
import { Plus as PlusIcon } from 'react-feather'
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  TablePagination,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import { useTheme } from '@material-ui/styles'

import { UserProfilesFilter } from './components'
import {
  Container,
  ContentHeader,
  FilterButton,
  LoadingFeedback,
  Page,
  Permitted,
} from 'components'
import { ProfilesTable } from 'components/Profiles'

import useFetch from 'hooks/useFetch'
import useFilter from 'hooks/useFilter'
import usePagination from 'hooks/usePagination'

import constants from 'constants/index'
import * as service from 'service'
import { routes } from 'Routes'

const UserProfilesMain = () => {
  const filter = useFilter()
  const theme = useTheme()
  const history = useHistory()
  const { pathname } = useLocation()

  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(10)
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true,
  })

  const isAdvisor = useMemo(
    () => pathname === routes.userProfiles.organizations,
    [pathname],
  )

  useEffect(() => {
    filter.setFilters('')
    // eslint-disable-next-line
  }, [pathname])

  const { response, isStillLoading, refresh } = useFetch(
    service.backoffice.userProfiles.get,
    {
      ...{ ...filter.filters },
      status: true,
      perPage,
      page,
      profileType: constants.profile.PROFILE_TYPE_CLIENT,
    },
    [page, perPage, filter.filters, isAdvisor],
    !isAdvisor,
  )

  const {
    response: advisorProfilesResponse,
    isStillLoading: isStillLoadingAdvisorProfiles,
    refresh: refreshAdvisorProfiles,
  } = useFetch(
    service.backoffice.advisorProfiles.get,
    {
      ...{ ...filter.filters },
      perPage,
      page,
    },
    [page, perPage, filter.filters, isAdvisor],
    isAdvisor,
  )

  const profiles =
    (isAdvisor
      ? advisorProfilesResponse?.data?.profiles
      : response?.data?.profiles) || []

  const redirectToCreate = () =>
    history.push(
      isAdvisor
        ? routes.userProfiles.newOrganizations
        : routes.userProfiles.new,
    )

  return (
    <Page title="Listagem Perfis de Usuário">
      <LoadingFeedback open={isStillLoading || isStillLoadingAdvisorProfiles} />
      <Container
        maxWidth={false}
        spacingXl={30}
        spacingLg={25}
        spacingMd={10}
        spacingSm={10}
      >
        <ContentHeader
          title="Listagem"
          menu={isAdvisor ? 'Perfis Advisor' : 'Perfis de usuário'}
          subtitle={isAdvisor ? 'Perfis Advisor' : 'Perfis de usuário'}
        >
          <Box ml={1}>
            <FilterButton setDrawerOpen={filter.setDrawerOpen} />
          </Box>
        </ContentHeader>
        <Card>
          <Box width="100%">
            <Grid container>
              <Grid item xs={6}>
                <Box
                  width="100%"
                  p={2}
                  height="100%"
                  display="flex"
                  alignItems="center"
                >
                  <Typography variant="h5">
                    {isAdvisor ? 'Perfis Advisor' : 'Perfis de usuário'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box p={2} display="flex" justifyContent="flex-end">
                  <Permitted
                    tag={
                      isAdvisor
                        ? constants.permissions.ADVISOR_PROFILE.CREATE
                        : constants.permissions.CLIENT_PROFILES.CREATE
                    }
                  >
                    <Button
                      onClick={redirectToCreate}
                      variant="outlined"
                      color="primary"
                      startIcon={<PlusIcon />}
                    >
                      Criar
                    </Button>
                  </Permitted>
                </Box>
              </Grid>
            </Grid>
            <Divider />
          </Box>
          <ProfilesTable
            profiles={profiles}
            refresh={isAdvisor ? refreshAdvisorProfiles : refresh}
            isAdvisor={isAdvisor}
          />
          <Box px={2} display="flex" justifyContent="flex-end">
            <TablePagination
              component="div"
              count={
                response?.data?.meta?.totalCount ||
                advisorProfilesResponse?.data?.meta?.totalCount ||
                0
              }
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              page={page - 1}
              rowsPerPage={perPage}
              rowsPerPageOptions={[5, 10, 25, 100]}
              labelRowsPerPage={isDesktop ? 'Por página' : ''}
              nextIconButtonProps={{ size: 'small' }}
              backIconButtonProps={{ size: 'small' }}
            />
          </Box>
        </Card>
      </Container>

      <UserProfilesFilter filter={filter} isAdvisor={isAdvisor} />
    </Page>
  )
}

export default UserProfilesMain
